/**
 * Plain-language explanations added by the learnability stage (DESIGN UD-18, UD-19): the headline's reading, the
 * notices' consequences, direction cues, the hotspot, reader, intake, diagnostics and bench explanations, and the cost
 * dialog's recommendation. Same conventions as `strings.ts`: plain constants and small functions, no DOM; none may
 * contain a word matched by `BANNED_WORDS` (test/unit/help-strings.test.ts). The interface never compares a value to the
 * reference point: the engine's `status` picks the sentence. Figures come from `model-card-figures.ts` and the bench
 * pilot (`docs/reports/bench-v2-pilot.md`, 2026-10-03); the dates below say when they were taken.
 */
import { CHANNEL_IDS } from '../engine/channels/types.ts';
import type { CalibratedStatus } from '../engine/ensemble/wording.ts';
import { figuresFor } from '../shared/model-figures.ts';
import type { ShortPointFigure } from '../shared/model-figures.ts';

const pct1 = (x: number): string => `${(x * 100).toFixed(1)}%`;

/** P3: one plain sentence under the status, keyed by the engine's status. `ref` is the reference index, formatted. */
export const STATUS_EXPLAIN: Record<
	CalibratedStatus,
	(ref: string, pooled: { n: string; flagged: string; bound: number }) => string
> = {
	flagged: (ref, p) =>
		`The index is above the reference point of ${ref}. In the reference corpus, ${p.flagged} of ${p.n} human documents were flagged (${pct1(Number(p.flagged) / Number(p.n.replace(/,/g, '')))}, at most ${pct1(p.bound)}). Read the marked passages before concluding anything.`,
	'at-reference': (ref) =>
		`The index equals the reference point of ${ref}: on the line, not above it. Read the marked passages; the channel evidence is the result.`,
	below: (ref) =>
		`The index is below the reference point of ${ref}. The channel evidence can still mark passages worth reading; a low index is not a clearance.`,
	abstain: (ref) =>
		`The index sits in the band around the reference point of ${ref} where the model does not call a status. Read the channel evidence instead.`,
	'abstain-interval': (ref) =>
		`The text-sampling interval spans the reference point of ${ref}, so the status is withheld: re-sampling this document moves the index across the line.`,
	'not-available': () =>
		'There is no calibrated index for this document: too little prose, or no calibrated model for its language. The channel evidence is still shown.',
};

const idx1 = (value: number): string => (value * 100).toFixed(1);
const words = (n: number): string => n.toLocaleString('en-US');

/**
 * CD-94 (REFIT-A audit §6, condition 1): the sentence for a length group whose calibration map cannot reach the reference
 * point. `ceiling` and `tStar` are on the value scale; the text shows index units (100 times), one decimal.
 */
export const UNREACHABLE_EXPLAIN = (maxWords: number, ceiling: number, tStar: number): string => {
	const who =
		maxWords >= 1_000_000
			? 'No document of this length'
			: `No document under ${words(maxWords)} words`;
	return `${who} reaches the reference point: the calibration map for this length stops at ${idx1(ceiling)}, below the reference point of ${idx1(tStar)}. A short text is never flagged, whatever it contains; read the channel evidence.`;
};

/**
 * The CD-94 sentence when the model's calibration map for the result's length group cannot reach the reference point and
 * the status is "below"; otherwise null. Under the C3 model no group is unreachable, so this is always null there.
 */
export function unreachableSentence(
	modelId: string | undefined,
	group: string | null | undefined,
	status: CalibratedStatus
): string | null {
	if (status !== 'below' || !group) return null;
	const u = figuresFor(modelId).unreachable[group];
	return u ? UNREACHABLE_EXPLAIN(u.maxWords, u.ceiling, u.tStar) : null;
}

/**
 * CD-108 (REFIT-A2 audit §7–§8, condition 2): the status sentence for a length group that has an operating point of its
 * own (the "short-text point", on the model's raw logit). It replaces the reference-point sentence, because the index of
 * such a text comes from a calibration map whose ceiling is below the reference point. Every figure comes from the
 * model's `shortPoint` figures; the cut is shown as a logit, never as an index or a percentage. (The wording avoids the
 * log-scale word that `BANNED_WORDS` matches; the diagnostics label for the number is "Model logit".)
 */
export const SHORT_POINT_EXPLAIN = {
	flagged: (p: ShortPointFigure, refIndex: string, capped = true): string =>
		`This text is under ${words(p.maxWords)} words, where this model flags at the ${p.name}: its raw model score (the logit) is at or above ${p.threshold.toFixed(3)}. ${shortIndexClause(p, refIndex, capped)} In the reference corpus, ${p.human.flagged} of ${words(p.human.n)} human documents under ${words(p.maxWords)} words were flagged there (${pct1(p.human.flagged / p.human.n)}, at most ${pct1(p.human.bound)}), among them ${p.esl.flagged} of ${words(p.esl.n)} essays by writers of English as a second language (at most ${pct1(p.esl.bound)}) and ${p.speeches.flagged} of ${words(p.speeches.n)} speeches (at most ${pct1(p.speeches.bound)}): a looser bar than the 1% per human group used for longer text.${p.crossValidated ? ` ${SHORT_RATES_CROSS_VALIDATED}` : ''} Read the marked passages before concluding anything.`,
	below: (p: ShortPointFigure): string =>
		`This text is under ${words(p.maxWords)} words: its raw model score (the logit) is below the ${p.name} of ${p.threshold.toFixed(3)}, the bar this model uses at this length (the index, from the short-text calibration map, stops at ${idx1(p.ceiling)}). The channel evidence can still mark passages worth reading; a low index is not a clearance.`,
} as const;

/**
 * UD-35 (REFIT-S): the short-text rates of a model fitted with a channel chosen on the same corpus are cross-validated, not
 * confirmed on a held-out set. Shown beside the rates wherever the model's `shortPoint.crossValidated` is set.
 */
export const SHORT_RATES_CROSS_VALIDATED =
	'These short-text rates are cross-validated on the fitting corpus, not confirmed on a held-out set.';

/**
 * Whether the short-text calibration map stops below the reference point, so that no text of the group can reach it by its
 * index (E-2: map top 0.943, reference point 98.8). The REFIT-S map's top is the clip, 0.990, above the reference point.
 * `referenceIndex` is the reference point in index units, as `calibratedIndex.referenceIndex`.
 */
export const ceilingBelowReference = (ceiling: number, referenceIndex: number): boolean =>
	ceiling * 100 < referenceIndex;

function shortIndexClause(p: ShortPointFigure, refIndex: string, capped: boolean): string {
	return capped
		? `The index beside it still comes from the short-text calibration map, which stops at ${idx1(p.ceiling)}, below the reference point of ${refIndex}.`
		: `The index beside it comes from the short-text calibration map, which stops at ${idx1(p.ceiling)}.`;
}

/**
 * The short-point sentence when the model has an operating point for the result's length group and the status is "below"
 * or a flag raised by that point; otherwise null (a flag raised by the reference point keeps the reference wording).
 * `referenceIndex` is the reference point in index units (98.8), as `calibratedIndex.referenceIndex`.
 */
export function shortPointSentence(
	modelId: string | undefined,
	ci: {
		group: string | null | undefined;
		status: CalibratedStatus;
		raisedBy?: 'reference' | 'length-group' | null;
		referenceIndex: number;
	}
): string | null {
	if (!ci.group) return null;
	const p = figuresFor(modelId).shortPoint[ci.group];
	if (!p) return null;
	if (ci.status === 'below') return SHORT_POINT_EXPLAIN.below(p);
	if (ci.status === 'flagged' && ci.raisedBy === 'length-group')
		return SHORT_POINT_EXPLAIN.flagged(
			p,
			ci.referenceIndex.toFixed(1),
			ceilingBelowReference(p.ceiling, ci.referenceIndex)
		);
	return null;
}

export const HEADLINE_EXPLAIN = {
	/** Beside the hero number, always. */
	notLines:
		'Not a percentage of machine text. Not proof. Not how sure to be that this document is machine-written: that depends on how much machine text you receive, as the conditions beside the dial say.',
	notLabel: 'What this number is not',
	/** When the calibrated index and the seed-rules composite sit on opposite sides of 50. */
	reconcile:
		'The index above is a calibrated model over the authorship channels. The composite beneath it, and the section strip, come from the seed rules on another scale, so they need not agree with it. The strip shows where the seed rules see more evidence, not the calibrated index per section.',
	reconcileLabel: 'Why the two numbers differ',
	/** The widest measured human cell, for the status line’s second sentence when the stratum is small. */
	widest: (label: string, flagged: string, n: string, bound: number) =>
		`The widest measured human cell is ${label}: ${flagged} of ${n} (at most ${pct1(bound)}).`,
	/** The final slice, as a second line of evidence. */
	finalSlice: (flagged: string, n: string, bound: number) =>
		`On the held-out final slice, ${flagged} of ${n} human documents were flagged (at most ${pct1(bound)}).`,
	translationNotAvailable:
		'MT-01 had too little text to measure; see its channel page for the reason.',
	translationRead:
		'The translation index is uncalibrated: it measures marks of machine translation into English and carries no error rate. Writers working in a second language can score here too.',
	strip:
		'Flat near 0 means the seed rules see little evidence in every section; it says nothing about the calibrated index.',
	termsLabel: 'Terms',
	strongest:
		'The eight highest channel values. A high value says the habit is strongly present in this text, not that the text is machine-written; open the channel to see what it counted and where.',
} as const;

/** The landing label: the authorship index is calibrated, everything else is not (replaces “uncalibrated” on screen one). */
export const APP_LABEL = (version: string): string =>
	`Prototype v${version} · authorship index calibrated; every other value uncalibrated`;

/** P5: direction cues. */
export const DIRECTION = {
	higher: 'Higher is more machine-like',
	lower: 'Lower is more machine-like',
	higherShort: '↑ machine-like',
	lowerShort: '↓ machine-like',
	chartAbove: 'above this line: more machine-like',
	chartBelow: 'below this line: more machine-like',
	deviationHelp:
		'Positive means machine-like for this channel, whichever way its raw value runs; negative means human-like.',
	readsOtherWay: 'Weigh this channel lightly for a document of this length.',
	hollowLegend: 'hollow marker: little evidence (κ below 0.20)',
	filledLegend: 'filled marker: enough text behind the estimate',
	/** Row label in the channel facts. */
	label: 'Direction',
} as const;

/** P13: what each notice means for the reader. */
export const NOTICE_EXPLAIN = {
	withheld:
		'These channels’ values are shown, but their hotspot and elevated flags are not: their reference comes from the display ramp, not from human text.',
	lengthDependent:
		'These channels’ values change with document length until their priors come from corpus values; weigh them lightly at this length.',
	abstained:
		'These channels had too little text to measure. Their absence lowers the evidence coverage; it is not a clean result for them.',
	hygiene:
		'The characters were normalised before analysis, so they do not change the index. Hidden characters inside words can be a sign of a text processed by a tool.',
	uncalibrated:
		'No calibrated model applied to this document, so the index is the seed-rules composite and carries no error rate.',
	patternsNone:
		'A pattern indicator is a rule of the seed’s ensemble that fires on a combination of channels: an assistance-residue pattern or a machine-translation pattern. None fired here.',
	patternsSome:
		'A pattern indicator is a rule of the seed’s ensemble that fires on a combination of channels. It is shown with the evidence that triggered it, never as a conclusion.',
	warning: 'A note from the intake about how the text was read.',
} as const;

/** P9: hotspots. */
export const HOTSPOT_EXPLAIN = {
	rowMeaning:
		'Each row is one stretch where one channel stands out. Open it to read the text with the channel’s marks; the score is the stretch’s evidence strength with its 90% interval.',
	ranking:
		'Order by the stretch’s score (its evidence strength), then by the smaller p against the reference, then by position in the document.',
	counts:
		'Entries are counted by flag (hotspot: above both the human reference and the document’s own background; elevated: above the reference only) and by kind (a segment of consecutive paragraphs, a single paragraph, or a whole chapter).',
	pHeader: 'p (reference / background)',
	openHint: 'Open in the reader',
	/** After the Order select, opening the engine-ranking definition. */
	orderTerm: 'what this order means',
} as const;

/** P7: the caption line that links the glossary term once per table where a column is headed "Score". */
export const SCORE_NOTE = {
	lead: 'Score: the ',
	term: 'evidence strength',
	entry: ' of the entry, from 0 to 100.',
	segment: ' of the segment, from 0 to 100.',
	chapter: ' of the chapter, from 0 to 100.',
} as const;

/** P10: reader. */
export const READER_EXPLAIN = {
	tip: (id: string, name: string, feature: string) => `${id} · ${name} · ${feature}`,
	tipMany: (n: string) => `${n} channels at this point; click for the inspector`,
	weightZero: 'shown for your judgement; not counted in the score',
	weight: (w: string) => `weight ${w}: how much this span counts toward the channel’s score`,
	focusCapWhy:
		'Three, because every set of four underline colours failed the colour-vision check; use the channel pages for the rest.',
	bracket: {
		hotspot: 'hotspot: above both the human reference and the document’s own background',
		elevated: 'elevated: above the human reference only',
	},
	layerAll: 'Every channel’s marks at once; the shade shows how many channels mark a span.',
	layerFocused: 'Only the focused channels’ marks, each with its own underline style.',
	shortcutsTitle: 'Keyboard shortcuts',
} as const;

/** P12: intake. */
export const INTAKE_EXPLAIN = {
	youGet: `You will get: an index with its caveats, ${CHANNEL_IDS.length} channel values reported separately, a map of the marked passages, and a list of hotspots.`,
	estimate: (time: string, words: string) => `About ${time} for ${words} words on this machine.`,
	estimateUnknown:
		'The analysis runs in this browser; a manuscript takes under a minute on a recent desktop.',
	formats:
		'PDF and Word files are converted to text first; running heads and page furniture are stripped. Markdown keeps its headings.',
	privacy: 'The text stays in this browser; nothing is uploaded for the analysis.',
} as const;

/** P14: diagnostics. */
export const DIAG_EXPLAIN = {
	model: 'Which calibrated model produced the index, and where this document sits in it.',
	document: 'What was read: the source, its size, its structure and its language.',
	intervals: 'How the intervals were computed and how many re-samples they used.',
	timing: 'How long each stage took in this browser.',
	abstained: 'Each channel that did not report, with the reason; open a channel for its page.',
	openChannel: (id: string) => `Open ${id}`,
} as const;

/** P11: bench. */
export const BENCH_EXPLAIN = {
	rewriteFlagged:
		'Sends every hotspot stretch to the model, up to three attempts each; keeps an attempt only if it reduces the targeted evidence, adds no new hotspot elsewhere and keeps the meaning. Your file is not changed; export the rewritten text separately.',
	rewriteDocument:
		'Targets the channels that are high across the whole document rather than in one place, by rewriting the stretches that carry most of their evidence.',
	repairTranslation:
		'Targets the marks that machine translation leaves, in the stretches that carry them. MT-01 (translation artefacts) is repaired where it marks a sentence. TR-01 (machine-leaning word choices) is addressed by replacing the marked words with plainer wording. TR-02 (human-leaning word choices) is protected: the rewrite never asks for its words, and an attempt that loses one of them is not kept. Each channel stays separate evidence.',
	rewriteChannel: 'Rewrites only the stretches this channel flagged.',
	rewriteStretch: 'Rewrites this one stretch.',
	localPass:
		'Runs in this browser, needs no key and no network, and reports which channels it reduced. It is reversible: the original text is kept.',
	economy:
		'Economy sends the requests in batches at about half the price; a round can take up to 24 hours and the job resumes if you close the page. Standard uses your key directly, one request at a time, at the list price.',
	keyFirst:
		'Enter an API key (header, “No key”) to start a deep rewrite. The local pass needs none.',
	expectedCost: 'Expected cost (includes the model’s reasoning tokens)',
	outcomes: {
		accepted: 'the rewrite passed every check and replaced the stretch',
		partial: 'some targeted channels were reduced, not all',
		failed: 'no attempt passed; the original is kept',
		keptBest:
			'no attempt passed every check; the closest safe attempt was kept and can be reverted',
		keptLenient:
			'no attempt passed every check; an attempt that passed the meaning checks and removed part of the pattern was kept at your request, and can be reverted',
		reverted: 'you or the ledger restored the original',
		declined:
			'the editor judged the passage better as it is and gave a reason; it was not rewritten',
	},
} as const;

/** UD-19: the cost dialog's recommendation. Figures from the bench pilot of 2026-10-03 (DESIGN ED-54, ED-58). */
export const RECOMMEND = {
	title: 'Recommendation',
	model:
		'Recommended model: Sonnet 5.5, the model the bench was measured with (operator decision ED-26). Haiku 5.5 costs about a twentieth as much per token; its rewrite quality has not been measured.',
	effort: 'High',
	effortLine: 'Recommended effort: High.',
	why: 'In the bench pilot (2026-10-03), High passed more stretches than Low (35 against 30 of 59 fiction units with signal) and removed more evidence (41.8 against 31.8 points), at about 1.5 times the cost and twice the time; the difference was not statistically significant (p = 0.33). On 25 non-fiction units the three efforts tied.',
	alternatives: {
		low: 'Low: about two thirds of the cost and half the time of High; passed fewer stretches in the pilot.',
		medium:
			'Medium: between Low and High in cost and time; in the pilot its results sat between them.',
		high: 'High: the pilot’s choice and the default.',
		xhigh: 'Extra high: not measured in the pilot; costs more than High with no measured gain.',
		max: 'Maximum: not measured in the pilot; costs more than High with no measured gain.',
	},
	chosenNote: (chosen: string) =>
		`${chosen} is selected; the recommended effort is High. The cost above is for the selected effort.`,
	useRecommended: 'Use the recommended effort',
	recommendedTag: '(recommended)',
} as const;

/** P12: a rounded duration for the intake estimate (the rounding steps are in `durationText`, `src/web/estimate.ts`). */
export const DURATION = {
	fewSeconds: 'a few seconds',
	seconds: (n: string) => `${n} seconds`,
	minute: '1 minute',
	minutes: (n: string) => `${n} minutes`,
	hour: '1 hour',
	hours: (n: string) => `${n} hours`,
} as const;
